// T-201 — fiche COURTE de création d'un prospect, pour les tenants dont la configuration la déclare (capacité `commercial.prospect_fiche_courte`).
// Entreprise ou commune (raison sociale, SIRET facultatif, code postal) · contact (nom, fonction, téléphone, e-mail) · besoin (liste éditable
// `prospect_besoins` de metier_config_listes) · commercial attribué (le commercial lui-même par défaut ; l'administrateur choisit).
// Elle ne crée rien elle-même : elle remet un `body` {entreprise, representant, cee} au parent, qui passe par le MÊME POST /api/metier/prospects
// et le MÊME contrôle de doublon (`/check-duplicate`) que le formulaire CEE. Le formulaire CEE reste celui d'OMV. Tokens --oh-* uniquement.
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { useConfigList } from "../../../hooks/useConfigList";
import { isLuhnValid } from "../../../hooks/useSiretSearch";

const chiffres = (v) => String(v || "").replace(/\D/g, "");

/** Valide la saisie ; rend un objet d'erreurs par champ (vide = valide). */
export function erreursFicheCourte(f, besoinsDisponibles) {
  const e = {};
  if (!String(f.raison_sociale || "").trim()) e.raison_sociale = "Indiquez l'entreprise ou la commune";
  if (!String(f.contact_nom || "").trim()) e.contact_nom = "Indiquez le nom du contact";
  const siret = chiffres(f.siret);
  if (siret && (siret.length !== 14 || !isLuhnValid(siret))) e.siret = "SIRET invalide (14 chiffres)";
  if (f.cp && chiffres(f.cp).length !== 5) e.cp = "Code postal : 5 chiffres";
  if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(f.email).trim())) e.email = "Adresse e-mail invalide";
  if (besoinsDisponibles && !f.besoin) e.besoin = "Choisissez le besoin";
  return e;
}

/** Le `body` remis au parent : mêmes blocs que le formulaire CEE (entreprise / representant / cee), vides retirés. */
export function bodyFicheCourte(f) {
  const net = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v != null));
  return {
    entreprise: net({ raison_sociale: String(f.raison_sociale).trim(), siret: chiffres(f.siret), cp: chiffres(f.cp) }),
    representant: net({ nom: String(f.contact_nom).trim(), fonction: String(f.fonction || "").trim(), tel: String(f.tel || "").trim(), email: String(f.email || "").trim() }),
    batiment: {}, technique: {},
    cee: net({ operation_cee: f.besoin }),         // champ réel lu par la normalisation des opérations (ETI-115) et la colonne « Opération »
  };
}

function Champ({ label, id, erreur, children }) {
  return (
    <div>
      <label htmlFor={id} className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
      {children}
      {erreur && <p className="text-xs mt-1" role="alert" style={{ color: "var(--oh-error-text)" }}>{erreur}</p>}
    </div>
  );
}
const STYLE_CHAMP = (erreur) => ({ background: "var(--oh-bg-surface)", border: `1px solid ${erreur ? "var(--oh-error-border)" : "var(--oh-border)"}`,
  color: "var(--oh-text)", borderRadius: 8, height: 36, padding: "0 12px", width: "100%", fontSize: 14, colorScheme: "light dark" });

export default function ProspectCreationRapide({ onSubmit, commerciaux = null, moi = null, checkDuplicate }) {
  const { items: besoins } = useConfigList("prospect_besoins");
  const [f, setF] = useState({ raison_sociale: "", siret: "", cp: "", contact_nom: "", fonction: "", tel: "", email: "", besoin: "", commercial_id: "" });
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [dup, setDup] = useState(false);
  const maj = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const avecListe = Array.isArray(besoins) && besoins.length > 0;

  const verifierDoublon = async () => {
    const s = chiffres(f.siret);
    if (s.length !== 14 || !checkDuplicate) { setDup(false); return; }
    const r = await checkDuplicate(s);
    setDup(!!(r && r.prospect && r.prospect.exists && r.prospect.match === "siret"));
  };

  const soumettre = async (ev) => {
    ev.preventDefault();
    const e = erreursFicheCourte(f, avecListe);
    setErreurs(e);
    if (Object.keys(e).length) return;
    setEnvoi(true);
    try { await onSubmit(bodyFicheCourte(f), null, { commercial_id: f.commercial_id || undefined }); } finally { setEnvoi(false); }
  };

  return (
    <form onSubmit={soumettre} className="space-y-4" data-testid="prospect-fiche-courte" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-3 text-[11px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Entreprise ou commune</div>
        <div className="sm:col-span-2"><Champ label="Raison sociale *" id="fc-rs" erreur={erreurs.raison_sociale}>
          <input id="fc-rs" data-testid="fc-raison-sociale" value={f.raison_sociale} onChange={maj("raison_sociale")} style={STYLE_CHAMP(erreurs.raison_sociale)} /></Champ></div>
        <Champ label="Code postal" id="fc-cp" erreur={erreurs.cp}>
          <input id="fc-cp" data-testid="fc-cp" inputMode="numeric" value={f.cp} onChange={maj("cp")} style={STYLE_CHAMP(erreurs.cp)} /></Champ>
        <div className="sm:col-span-3"><Champ label="SIRET (facultatif)" id="fc-siret" erreur={erreurs.siret}>
          <input id="fc-siret" data-testid="fc-siret" inputMode="numeric" value={f.siret} onChange={maj("siret")} onBlur={verifierDoublon} style={STYLE_CHAMP(erreurs.siret)} /></Champ>
          {dup && <p className="text-xs mt-1" data-testid="fc-doublon" role="status" style={{ color: "var(--oh-info-text)" }}>Un prospect avec ce SIRET existe déjà : il vous sera proposé de l'ouvrir ou d'en créer un nouveau.</p>}
        </div>
        <div className="sm:col-span-3 text-[11px] uppercase tracking-wide mt-1" style={{ color: "var(--oh-text-muted)" }}>Contact</div>
        <Champ label="Nom *" id="fc-nom" erreur={erreurs.contact_nom}>
          <input id="fc-nom" data-testid="fc-contact-nom" value={f.contact_nom} onChange={maj("contact_nom")} style={STYLE_CHAMP(erreurs.contact_nom)} /></Champ>
        <Champ label="Fonction" id="fc-fonction"><input id="fc-fonction" data-testid="fc-fonction" value={f.fonction} onChange={maj("fonction")} style={STYLE_CHAMP()} /></Champ>
        <Champ label="Téléphone" id="fc-tel"><input id="fc-tel" data-testid="fc-tel" type="tel" value={f.tel} onChange={maj("tel")} style={STYLE_CHAMP()} /></Champ>
        <div className="sm:col-span-3"><Champ label="E-mail" id="fc-email" erreur={erreurs.email}>
          <input id="fc-email" data-testid="fc-email" type="email" value={f.email} onChange={maj("email")} style={STYLE_CHAMP(erreurs.email)} /></Champ></div>
        <div className="sm:col-span-3 text-[11px] uppercase tracking-wide mt-1" style={{ color: "var(--oh-text-muted)" }}>Besoin et suivi</div>
        <Champ label={avecListe ? "Besoin *" : "Besoin"} id="fc-besoin" erreur={erreurs.besoin}>
          {avecListe ? (
            <select id="fc-besoin" data-testid="fc-besoin" value={f.besoin} onChange={maj("besoin")} style={STYLE_CHAMP(erreurs.besoin)}>
              <option value="">À choisir</option>
              {besoins.map((b) => <option key={b.id || b.label} value={b.label || b.id}>{b.label || b.id}</option>)}
            </select>
          ) : (
            <input id="fc-besoin" data-testid="fc-besoin" value={f.besoin} onChange={maj("besoin")} placeholder="Liste « prospect_besoins » non configurée : saisie libre" style={STYLE_CHAMP()} />
          )}
        </Champ>
        <Champ label="Commercial attribué" id="fc-commercial">
          {Array.isArray(commerciaux) && commerciaux.length > 0 ? (
            <select id="fc-commercial" data-testid="fc-commercial" value={f.commercial_id} onChange={maj("commercial_id")} style={STYLE_CHAMP()}>
              <option value="">Aucun</option>
              {commerciaux.map((c) => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
            </select>
          ) : (
            <div data-testid="fc-commercial-moi" className="text-sm" style={{ color: "var(--oh-text)", lineHeight: "36px" }}>{moi || "Vous"}</div>
          )}
        </Champ>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={envoi} data-testid="fc-creer">{envoi ? "Création…" : "Créer le prospect"}</Button>
      </div>
    </form>
  );
}
